import { createTheme, Theme } from '@mui/material/styles';

// Define the interface locally to avoid circular imports
export interface CustomerThemeConfig {
  id: string;
  name: string;
  description?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  logo?: string;
  favicon?: string;
  customCSS?: string;
  fontFamily?: string;
  borderRadius?: number;
  spacing?: number;
  loginBackgroundUrl?: string;
  /** When set, login/set-password use this color instead of primary (e.g. white for logos that clash with primary). */
  loginBackgroundColor?: string;
  /** Portal top bar. Dark brands (e.g. ROC Telecom) set this to black so the logo reads correctly. */
  headerBackgroundColor?: string;
  headerColor?: string;
  supportEmail?: string;
}

export interface CustomerTheme extends Theme {
  customer: CustomerThemeConfig;
}

// Helper function to create customer theme
function createCustomerTheme(
  primaryColor: string,
  secondaryColor: string,
  accentColor?: string,
  fontFamily?: string,
  borderRadius?: number
) {
  return createTheme({
    typography: {
      fontFamily: fontFamily || ['Roboto', 'sans-serif'].join(','),
    },
    palette: {
      primary: {
        main: primaryColor,
        light: `${primaryColor}20`,
        dark: `${primaryColor}80`,
      },
      secondary: {
        main: secondaryColor,
        contrastText: '#FFFFFF',
        light: `${secondaryColor}20`,
        dark: `${secondaryColor}80`,
      },
      accent: {
        main: accentColor || secondaryColor,
        contrastText: '#FFFFFF',
      },
      neutral: {
        main: '#7A7A7A',
        light: '#323232',
      },
    },
    shape: {
      borderRadius: borderRadius || 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius || 8,
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: '0.5px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius || 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius || 8,
          },
        },
      },
    },
  });
}

// Customer theme configurations
export const customerThemes: Record<string, CustomerTheme> = {
  recycle: {
    ...createCustomerTheme('#353637', '#81C41D', '#2E7D32'),
    customer: {
      id: 'recycle',
      name: 'Recycle Theme',
      description: 'Default recycling company theme',
      primaryColor: '#353637',
      secondaryColor: '#81C41D',
      accentColor: '#2E7D32',
      logo: '/assets/images/recycle-logo.png',
      favicon: '/favicon.ico',
      supportEmail: 'support@electronicsalpha.com',
      fontFamily: 'Roboto, sans-serif',
      borderRadius: 8,
      spacing: 8,
    },
  } as CustomerTheme,

  amwaste: {
    ...createCustomerTheme('#1B365D', '#FF6B35', '#E63946'),
    customer: {
      id: 'amwaste',
      name: 'AMWaste Theme',
      description: 'AMWaste company branding',
      primaryColor: '#1B365D',
      secondaryColor: '#FF6B35',
      accentColor: '#E63946',
      logo: '/assets/images/amwaste.png',
      favicon: '/favicon.ico',
      supportEmail: 'support@electronicsalpha.com',
      fontFamily: 'Inter, sans-serif',
      borderRadius: 12,
      spacing: 10,
    },
  } as CustomerTheme,

  evolution: {
    ...createCustomerTheme('#2C5530', '#F4A261', '#E76F51'),
    customer: {
      id: 'evolution',
      name: 'Evolution Theme',
      description: 'Evolution company branding',
      primaryColor: '#2C5530',
      secondaryColor: '#F4A261',
      accentColor: '#E76F51',
      logo: '/assets/images/evolution.png',
      favicon: '/favicon.ico',
      supportEmail: 'support@electronicsalpha.com',
      fontFamily: 'Poppins, sans-serif',
      borderRadius: 16,
      spacing: 12,
    },
  } as CustomerTheme,

  nerdit: {
    ...createCustomerTheme('#2D3748', '#38B2AC', '#ED8936'),
    customer: {
      id: 'nerdit',
      name: 'Nerdit Theme',
      description: 'Nerdit company branding',
      primaryColor: '#2D3748',
      secondaryColor: '#38B2AC',
      accentColor: '#ED8936',
      logo: '/assets/images/nerdit.png',
      favicon: '/favicon.ico',
      supportEmail: 'support@electronicsalpha.com',
      fontFamily: 'Source Sans Pro, sans-serif',
      borderRadius: 6,
      spacing: 6,
    },
  } as CustomerTheme,

  phoenix: {
    ...createCustomerTheme('#7C2D12', '#F59E0B', '#DC2626'),
    customer: {
      id: 'phoenix',
      name: 'Phoenix Theme',
      description: 'Phoenix Solutions branding',
      primaryColor: '#7C2D12',
      secondaryColor: '#F59E0B',
      accentColor: '#DC2626',
      logo: '/assets/images/phoenix_solutions.svg',
      favicon: '/favicon.ico',
      supportEmail: 'support@electronicsalpha.com',
      fontFamily: 'Open Sans, sans-serif',
      borderRadius: 10,
      spacing: 8,
    },
  } as CustomerTheme,

  assettech: {
    ...createCustomerTheme('#184863', '#3e765d', '#2a9d8f'),
    customer: {
      id: 'assettech',
      name: 'Assettech Theme',
      description: 'Assettech company branding for atportal.co.uk',
      primaryColor: '#184863',
      secondaryColor: '#3e765d',
      accentColor: '#2a9d8f',
      logo: 'https://genericassets.s3.us-east-1.amazonaws.com/asset_tech_icon.avif',
      favicon: '/favicon.ico',
      loginBackgroundUrl: 'https://genericassets.s3.us-east-1.amazonaws.com/manchester_skyline_zoomed_out.avif',
      supportEmail: 'info@asset-technology.co.uk',
      fontFamily: 'Montserrat, sans-serif',
      borderRadius: 10,
      spacing: 8,
    },
  } as CustomerTheme,

  drma: {
    ...createCustomerTheme('#2b3990', '#667eea', '#4a90e2'),
    customer: {
      id: 'drma',
      name: 'DRM America',
      description: 'DRM America Inc.',
      primaryColor: '#2b3990',
      secondaryColor: '#667eea',
      accentColor: '#4a90e2',
      logo: '/assets/images/drmw_logo.png',
      favicon: '/favicon.ico',
      supportEmail: 'info@drmamericainc.com',
      fontFamily: 'Roboto, sans-serif',
      borderRadius: 8,
      spacing: 8,
      loginBackgroundColor: '#f5f5f5',
    },
  } as CustomerTheme,

  synetic: {
    ...createCustomerTheme('#225979', '#63B4DC', '#828484', 'Gabarito, sans-serif', 10),
    customer: {
      id: 'synetic',
      name: 'Synetic Theme',
      description: 'Synetic Technologies branding for synetic.tech',
      primaryColor: '#225979',
      secondaryColor: '#63B4DC',
      accentColor: '#828484',
      logo: '/assets/images/synetic_logo.svg',
      favicon: '/favicon.ico',
      supportEmail: 'info@synetic.com',
      fontFamily: 'Gabarito, sans-serif',
      borderRadius: 10,
      spacing: 8,
      loginBackgroundColor: '#ffffff',
    },
  } as CustomerTheme,

  roctelecom: {
    ...createCustomerTheme('#3CCB6E', '#6B5CFF', '#111111', 'Montserrat, sans-serif', 10),
    customer: {
      id: 'roctelecom',
      name: 'ROC Telecom',
      description: 'ROC Telecom portal branding from roctelecom.com',
      primaryColor: '#3CCB6E',
      secondaryColor: '#6B5CFF',
      accentColor: '#111111',
      logo: '/assets/images/roctelecom_logo.png',
      favicon: '/assets/images/roctelecom_logo.png',
      supportEmail: 'info@roctelecom.com',
      fontFamily: 'Montserrat, sans-serif',
      borderRadius: 10,
      spacing: 8,
      loginBackgroundColor: '#ffffff',
      headerBackgroundColor: '#ffffff',
      headerColor: '#111111',
      customCSS: `
        .roctelecom-accent-bar {
          height: 3px;
          width: 100%;
          background: linear-gradient(90deg, #3CCB6E 0%, #6B5CFF 100%);
        }
      `,
    },
  } as CustomerTheme,

  dothavlu: {
    ...createCustomerTheme('#353637', '#81C41D', '#2E7D32', 'Roboto, sans-serif', 8),
    customer: {
      id: 'dothavlu',
      name: 'Dothavlu Theme',
      description: 'Dothavlu customer branding',
      primaryColor: '#353637',
      secondaryColor: '#81C41D',
      accentColor: '#2E7D32',
      favicon: '/favicon.ico',
      supportEmail: 'support@electronicsalpha.com',
      fontFamily: 'Roboto, sans-serif',
      borderRadius: 8,
      spacing: 8,
    },
  } as CustomerTheme,
};

// Extend MUI theme types for custom palette colors
declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    accent: Palette['primary'];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
    accent?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    accent: true;
  }
}
